import { appKey } from '@app/shared/brand'
import {
  AppWindow,
  ArrowLeft,
  Minimize2,
  PanelLeftClose,
  PanelRightClose,
  TableOfContents,
  Users,
  X,
} from 'lucide-react'
import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { OpenDocs } from '../../routes/studio/OpenDocs'
import {
  Button,
  orderedPageActions,
  type PageActionItem,
  SearchField,
  SegmentedControl,
  Tooltip,
} from '../../ui'
import type { CompanionView } from '../../ui/Companion'
import { RecordActions } from '../../ui/RecordActions'
import '../../ui/docbar.css'
import { migrateDocStorage } from '../doc-storage-migrations'
import type { OpenDoc } from '../open-docs'

const NAV_WIDTH_KEY = appKey('docNavWidth')
const NAV_OPEN_KEY = appKey('docNavOpen')
const NAV_MODE_KEY = appKey('docNavMode')

/* Wide enough for a contents line, narrow enough to leave the paper the room. */
const NAV_MIN = 200
const NAV_MAX = 420
const NAV_DEFAULT = 256

type NavMode = 'contents' | 'open'

function readStored(key: string): string | null {
  try {
    migrateDocStorage()
    return localStorage.getItem(key)
  } catch {
    return null
  }
}
function store(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* Held for this visit only. */
  }
}

/**
 * Whether the left bar is out, and which view it shows.
 *
 * Beside the paper, what a person chose is remembered. Where the bar covers
 * the paper instead, it starts folded and is opened for the moment, so a
 * phone opens on the document rather than on chrome.
 */
export function useDocBarState(roomy: boolean) {
  const [navPref, setNavPref] = useState(() => readStored(NAV_OPEN_KEY) !== '0')
  const [navNow, setNavNow] = useState(false)
  const [mode, setModeRaw] = useState<NavMode>(() =>
    readStored(NAV_MODE_KEY) === 'open' ? 'open' : 'contents',
  )
  /* A bar opened over the paper is for the moment; going to the desk width
     and back starts it folded again. */
  useEffect(() => {
    if (roomy) setNavNow(false)
  }, [roomy])

  const setNavOpen = useCallback(
    (open: boolean) => {
      if (roomy) {
        setNavPref(open)
        store(NAV_OPEN_KEY, open ? '1' : '0')
      } else setNavNow(open)
    },
    [roomy],
  )
  const setMode = useCallback((m: NavMode) => {
    setModeRaw(m)
    store(NAV_MODE_KEY, m)
  }, [])

  return { navOpen: roomy ? navPref : navNow, setNavOpen, mode, setMode }
}

/**
 * The document alone on the page: both bars and both rails go, and one small
 * control stays in the top corner to bring them back, as does Escape. The
 * browser's own full screen is left to the browser — taking the whole display
 * hides the reader's tabs and announces itself, which is more than putting the
 * chrome away asks for.
 */
export function useFullScreen() {
  const [full, setFull] = useState(false)
  useEffect(() => {
    if (!full) return
    function onKey(e: KeyboardEvent) {
      /* An Escape something else has already answered — a menu, a popover —
         was that thing's, not the page's. */
      if (e.key === 'Escape' && !e.defaultPrevented) setFull(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [full])
  const enter = useCallback(() => setFull(true), [])
  const exit = useCallback(() => setFull(false), [])
  return { full, enter, exit }
}

/**
 * Hands keyboard focus on when a bar folds or opens, or the document goes full
 * screen and back. Each of those unmounts the control that was pressed, which
 * drops focus to the document; the control that now stands for the same thing
 * takes it instead. Controls are named by `data-doc-focus`, the first one on
 * screen wins, and focus moves only when it was lost — a reader whose focus is
 * still in the document keeps it there.
 */
export function useDocFocus() {
  const next = useRef<readonly string[] | null>(null)
  const [asked, setAsked] = useState(0)
  useEffect(() => {
    const keys = next.current
    if (asked === 0 || !keys) return
    next.current = null
    const held = document.activeElement
    if (held && held !== document.body) return
    for (const key of keys) {
      const el = document.querySelector<HTMLElement>(
        `[data-doc-focus="${key}"]`,
      )
      if (el) {
        el.focus()
        return
      }
    }
  }, [asked])
  return useCallback((...keys: string[]) => {
    next.current = keys
    setAsked((n) => n + 1)
  }, [])
}

/**
 * The left bar of an open document: the way back and the document's name, then
 * either the document's own contents or the documents the person has open.
 */
export function DocNavBar({
  title,
  onBack,
  views,
  mode,
  onMode,
  current,
  onClose,
  phone = false,
  foot,
}: {
  title: ReactNode
  onBack: () => void
  /** The document's own navigation — a book's contents. */
  views: CompanionView[]
  mode: NavMode
  onMode: (m: NavMode) => void
  current: OpenDoc | null
  onClose: () => void
  /** Rising over the document in a hand: the bar's slot is the way back, so
      the head holds the name and a close, and nothing is resized. */
  phone?: boolean
  /** What stands at the bar's foot — the account, in a hand. */
  foot?: ReactNode
}) {
  const [width, setWidthRaw] = useState(() => {
    const stored = Number(readStored(NAV_WIDTH_KEY))
    return stored >= NAV_MIN && stored <= NAV_MAX ? stored : NAV_DEFAULT
  })
  const setWidth = useCallback((w: number) => {
    const next = Math.round(Math.min(Math.max(w, NAV_MIN), NAV_MAX))
    setWidthRaw(next)
    store(NAV_WIDTH_KEY, String(next))
  }, [])
  const bar = useRef<HTMLElement>(null)
  const dragging = useRef(false)
  useEffect(() => {
    function move(e: PointerEvent) {
      if (!dragging.current || !bar.current) return
      const box = bar.current.getBoundingClientRect()
      /* Measured from the edge the bar starts at, so it holds in either
         writing direction. */
      setWidth(
        document.dir === 'rtl' ? box.right - e.clientX : e.clientX - box.left,
      )
    }
    function up() {
      if (!dragging.current) return
      dragging.current = false
      document.body.classList.remove('is-resizing')
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
  }, [setWidth])

  /* Risen over the document it is put away by Escape, as any sheet is. */
  useEscapeToClose(!!phone, onClose)

  /* A document with nothing of its own to navigate — a page — shows the open
     documents, and offers no switch onto nothing. */
  const nav = views[0]
  const showing: NavMode = nav ? mode : 'open'

  return (
    <aside
      ref={bar}
      className="docbar docbar--nav"
      aria-label="Document"
      /* A property rather than a width, so a layout that sizes the bar by
         itself — rising over the document in a hand — is free to. */
      style={{ '--docnav-w': `${width}px` } as CSSProperties}
    >
      <div className="docbar__head">
        {phone ? null : (
          <Tooltip label="Back">
            <Button variant="quiet" icon aria-label="Back" onClick={onBack}>
              <ArrowLeft size={16} aria-hidden />
            </Button>
          </Tooltip>
        )}
        <h1 className="docbar__title">{title}</h1>
        {phone ? (
          <SheetClose label="Close" focusKey="nav-fold" onClose={onClose} />
        ) : (
          <Tooltip label="Fold the sidebar">
            <Button
              variant="quiet"
              icon
              aria-label="Fold the sidebar"
              data-doc-focus="nav-fold"
              onClick={onClose}
            >
              <PanelLeftClose size={16} aria-hidden />
            </Button>
          </Tooltip>
        )}
      </div>
      {/* The strip is drawn even with one tab: it names the list beneath it as
         the documents open, which is not part of the one on screen. */}
      <div className="docbar__switch">
        <SegmentedControl
          size="sm"
          ariaLabel="What the sidebar shows"
          value={nav ? showing : 'open'}
          onChange={onMode}
          options={[
            ...(nav
              ? [
                  {
                    value: 'contents' as const,
                    label: nav.label,
                    icon: TableOfContents,
                  },
                ]
              : []),
            {
              value: 'open' as const,
              // Beside a second tab the screen says "docs": the full word would
              // not let the two share the bar evenly. A screen reader hears
              // the full name.
              label: nav ? 'Open docs' : 'Open documents',
              ariaLabel: 'Open documents',
              icon: AppWindow,
            },
          ]}
        />
      </div>
      {showing === 'contents' && nav?.search ? (
        <div className="docbar__search">
          <SearchField
            value={nav.search.value}
            onChange={nav.search.onChange}
            placeholder={nav.search.placeholder}
            ariaLabel={nav.search.ariaLabel ?? 'Search'}
          />
        </div>
      ) : null}
      <div className="docbar__body">
        {showing === 'contents' && nav ? (
          nav.render()
        ) : (
          <OpenDocs current={current} currentTitle={title} />
        )}
      </div>
      {foot ? <div className="docbar__foot">{foot}</div> : null}
      {/* The split is the person's to set, by pointer or by its arrow keys. */}
      {phone ? null : (
        <div
          className="docbar__resize"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize the sidebar"
          aria-valuenow={width}
          aria-valuemin={NAV_MIN}
          aria-valuemax={NAV_MAX}
          tabIndex={0}
          onPointerDown={(e) => {
            dragging.current = true
            document.body.classList.add('is-resizing')
            e.preventDefault()
          }}
          onKeyDown={(e) => {
            const step = e.shiftKey ? 64 : 16
            const grow = document.dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight'
            const shrink = document.dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft'
            if (e.key === grow) setWidth(width + step)
            else if (e.key === shrink) setWidth(width - step)
            else return
            e.preventDefault()
          }}
        />
      )}
    </aside>
  )
}

/**
 * A bar folded to its icons, floating at its own edge over the canvas. Each
 * side keeps its own tools, and an icon opens the bar at what it names.
 */
export function DocRail({
  side,
  label,
  children,
}: {
  side: 'nav' | 'tools'
  label: string
  children: ReactNode
}) {
  return (
    <nav className={`docrail docrail--${side}`} aria-label={label}>
      {children}
    </nav>
  )
}

/** One icon on a rail, named by its tooltip. */
export function RailButton({
  label,
  onClick,
  focusKey,
  mark = false,
  children,
}: {
  label: string
  onClick: () => void
  /** The name focus is handed on by — see `useDocFocus`. */
  focusKey?: string
  /** What is behind the button wants the reader: a dot on its icon. */
  mark?: boolean
  children: ReactNode
}) {
  return (
    <Tooltip label={label}>
      <button
        type="button"
        className="docrail__button"
        aria-label={label}
        data-doc-focus={focusKey}
        onClick={onClick}
      >
        {mark ? (
          <span className="docrail__glyph">
            {children}
            <span className="docrail__mark" />
          </span>
        ) : (
          children
        )}
      </button>
    </Tooltip>
  )
}

/**
 * Escape puts a risen sheet away, unless something over it — a menu, a
 * popover — has already answered the key, and then the sheet consumes it so
 * one press closes one surface.
 */
function useEscapeToClose(active: boolean, onClose: () => void) {
  useEffect(() => {
    if (!active) return
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      e.preventDefault()
      onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [active, onClose])
}

/** The way out of a sheet risen over the document: one close at its head. */
export function SheetClose({
  label,
  focusKey,
  onClose,
}: {
  label: string
  focusKey?: string
  onClose: () => void
}) {
  return (
    <Tooltip label={label}>
      <Button
        variant="quiet"
        icon
        aria-label={label}
        data-doc-focus={focusKey}
        onClick={onClose}
      >
        <X size={16} aria-hidden />
      </Button>
    </Tooltip>
  )
}

/**
 * The Actions of an open document, as a sheet risen over it in a hand: the
 * page's actions in their type order, one row each, glyph and verb. Picking
 * one puts the sheet away, since what it opens is then on screen.
 */
export function ActionsSheet({
  items,
  onClose,
}: {
  items: PageActionItem[]
  onClose: () => void
}) {
  useEscapeToClose(true, onClose)
  return (
    <section className="docrise" aria-label="Actions">
      <div className="docbar__head">
        <h2 className="docbar__title">Actions</h2>
        <SheetClose label="Close" focusKey="actions-close" onClose={onClose} />
      </div>
      <div className="docbar__body">
        <RecordActions
          title="Actions"
          actions={orderedPageActions(items).map((i) => ({
            label: i.open ? `Close ${i.label.toLowerCase()}` : i.label,
            icon: i.open ? X : i.icon,
            figure: i.waiting ? String(i.waiting) : undefined,
            holding: !i.waiting && i.holding,
            onSelect: () => {
              onClose()
              i.onSelect()
            },
          }))}
        />
      </div>
    </section>
  )
}

/** The one bar of an open document in a hand: a row of slots. */
export function PhoneBar({ children }: { children: ReactNode }) {
  return (
    <nav className="docphone" aria-label="Document">
      {children}
    </nav>
  )
}

/**
 * One slot of the phone bar: an icon and its word. A slot that opens a sheet
 * says whether it is open, and pressing it again puts the sheet away.
 */
export function PhoneSlot({
  label,
  icon,
  onClick,
  open,
  focusKey,
  mark,
}: {
  label: string
  icon: ReactNode
  onClick: () => void
  /** Whether the sheet this slot opens is showing; absent for a slot that goes somewhere. */
  open?: boolean
  focusKey?: string
  /** Something behind the slot wants the reader: somebody else is here, or
      something is waiting, in ink; a view holding a problem, in warning. */
  mark?: 'ink' | 'warning'
}) {
  return (
    <button
      type="button"
      className="docphone__slot"
      aria-expanded={open}
      data-doc-focus={focusKey}
      onClick={onClick}
    >
      <span className="docphone__glyph">
        {icon}
        {mark ? <span className="docphone__mark" data-tone={mark} /> : null}
      </span>
      {label}
    </button>
  )
}

/** What the switch for reading together is called, wherever it is drawn. */
export function togetherLabel(on: boolean) {
  return on ? 'Stop reading together' : 'Read together'
}

/**
 * The switch for reading together. A dot on it says somebody else is here
 * while it is off, so the rail says there is company without showing who.
 */
export function TogetherToggle({
  on,
  others,
  onToggle,
  place,
}: {
  on: boolean
  others: number
  onToggle: () => void
  place: 'rail' | 'head'
}) {
  const label = togetherLabel(on)
  const glyph = (
    <span className="together-toggle">
      <Users size={16} aria-hidden />
      {!on && others > 0 ? <span className="together-toggle__mark" /> : null}
    </span>
  )
  return (
    <Tooltip label={label}>
      {place === 'rail' ? (
        <button
          type="button"
          className="docrail__button"
          aria-label={label}
          aria-pressed={on}
          onClick={onToggle}
        >
          {glyph}
        </button>
      ) : (
        <Button
          variant="quiet"
          icon
          aria-label={label}
          aria-pressed={on}
          onClick={onToggle}
        >
          {glyph}
        </Button>
      )}
    </Tooltip>
  )
}

/** The way from the band back to its rail. */
export function FoldTools({ onClose }: { onClose: () => void }) {
  return (
    <Tooltip label="Fold the panel">
      <Button
        variant="quiet"
        icon
        aria-label="Fold the panel"
        data-doc-focus="tools-fold"
        onClick={onClose}
      >
        <PanelRightClose size={16} aria-hidden />
      </Button>
    </Tooltip>
  )
}

/** The one control left on screen while the document is alone on it. */
export function ExitFullScreen({ onExit }: { onExit: () => void }) {
  return (
    <div className="docrail-exit">
      <Tooltip label="Exit full screen">
        <Button
          variant="quiet"
          icon
          aria-label="Exit full screen"
          data-doc-focus="exit-full"
          onClick={onExit}
        >
          <Minimize2 size={16} aria-hidden />
        </Button>
      </Tooltip>
    </div>
  )
}
